import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import { tag_colorTagPendingText } from 'ext-ui/style/colors'
import AlertShieldIcon from 'ext-ui/svg/icon/AlertShieldIcon'
import ArrowIcon from 'ext-ui/svg/icon/ArrowIcon'
import CardsNewIcon from 'ext-ui/svg/icon/CardsNew'
import LockedIcon from 'ext-ui/svg/icon/LockedIcon'
import React from 'react'
import Box from 'src/components/Box/Box'
import LoadingShimmer from 'src/components/LoadingShimmer/LoadingShimmer'
import { useTranslate } from 'src/localization/Trns'

import styles from './BillPayBankAccounts.style'

/**
 * Bill Pay Milestone 1 — the "Bank Account" settings surface.
 *
 * A routing-free feature widget (values in via props) rendering Richa's
 * two-card, KYB-gated design (extend-design-render/invoice-pay-prototype,
 * `settings/bank-account-v2`): a 50/50 pair of content cards —
 *   • Business Verification (KYB)  • Bank Account
 * whose state is a function of (KYB status) × (a funding account exists).
 *
 * States (see [[project_invoice_pay]] "M1 REAL DESIGN"):
 *  - unverified → "Verification required" banner; Bank Account LOCKED.
 *  - pending    → KYB "Verifying your business"; Bank Account still LOCKED.
 *  - denied     → terminal verification failure; Bank Account stays LOCKED.
 *  - verified, no account → Bank Account UNLOCKS → "Add bank account" CTA.
 *  - verified + pending account → "Pending verification", no CTA.
 *  - verified + active account  → account details shown, no CTA.
 *  - verified + unknown account status → "Review required", no CTA.
 *
 * Banner/CTA icons use ext-ui's own set (the prototype's heroicons map onto
 * `AlertShieldIcon`/`LockedIcon`/`ArrowIcon`/`CardsNewIcon` — see the
 * [[reference_design_render_repo]] mapping). No clock icon exists for the
 * pending banner yet, so that one state renders icon-less for now.
 */

export type BillPayKybStatus = 'unverified' | 'pending' | 'verified' | 'denied'

export interface BillPayFundingAccount {
  /** Omitted for backwards-compatible callers that already represent only
   * active accounts. Pending/review states remain present but non-actionable.
   * `linking` is collected-but-not-yet-on-the-connection: no CTA at all. */
  status?: 'active' | 'linking' | 'pending' | 'unverified' | 'review'
  bankName?: string
  accountType?: string
  routingNumber?: string
  last4?: string
}

interface CardBanner {
  // 'default-wcag' (not 'default') for the locked banner: it's the only way the
  // ext-ui AlertPanel wrapper exposes textStyle="monochrome" — the neutral grey
  // text + grey border the prototype's locked banner uses. See styles.lockedBanner.
  variant: 'warning' | 'severe' | 'default-wcag'
  icon?: React.ReactElement
  title: string
  description: string
}

const Card: React.FC<
  React.PropsWithChildren<{
    title: string
    banner?: CardBanner
    footer?: React.ReactNode
    testId?: string
  }>
> = ({ title, banner, footer, children, testId }) => (
  <Box noPadding noShadow outlineBorder css={styles.card} testId={testId}>
    {banner && (
      // ext-ui AlertPanel renders `title` as a native tooltip only and shows
      // `children` as the visible message — so the bold title + description
      // are composed into children (inline spans to stay valid inside the
      // panel's subtitle element).
      <AlertPanel
        variant={banner.variant}
        icon={banner.icon}
        // The prototype strips the ADS AlertPanel's full rounded box border to a
        // bottom-only divider; the locked banner also goes grey. See
        // styles.bannerFlush / styles.lockedBanner.
        css={
          banner.variant === 'default-wcag'
            ? styles.lockedBanner
            : styles.bannerFlush
        }
      >
        <span
          css={
            banner.variant === 'default-wcag'
              ? styles.bannerLocked
              : banner.variant === 'severe'
                ? styles.bannerSevere
                : styles.bannerWarning
          }
        >
          <span
            // Both variants render the title at medium weight / 14px (bannerTitle);
            // warning inherits the wrapper's amber, locked title goes navy
            // (primary) while its description stays the wrapper's grey.
            css={
              banner.variant === 'default-wcag'
                ? styles.lockedTitle
                : styles.bannerTitle
            }
          >
            {banner.title}
          </span>
          <span>{banner.description}</span>
        </span>
      </AlertPanel>
    )}
    <div css={styles.body}>
      <h2 className="font-h3">{title}</h2>
      {children}
    </div>
    {footer && <div css={styles.footer}>{footer}</div>}
  </Box>
)

const Field: React.FC<React.PropsWithChildren<{ label: string }>> = ({
  label,
  children,
}) => (
  <div css={styles.field}>
    <span css={styles.fieldLabel}>{label}</span>
    <span css={styles.fieldValue}>{children}</span>
  </div>
)

/** Whether the realtime provider projection behind the account detail rows has
 * arrived. Status comes from the durable connection and stays trustworthy in
 * every state; only the detail rows depend on this. */
export type BillPayAccountDetailsState = 'ready' | 'loading' | 'unavailable'

/** The rows fed by the realtime projection — Status is not among them, it comes
 * off the durable connection. One skeleton per real row so the card keeps its
 * height and nothing shifts when the projection lands; widths ape the typical
 * value length per field. */
const ACCOUNT_DETAIL_SKELETONS: { label: string; width: string }[] = [
  { label: 'Bank Name', width: '180px' },
  { label: 'Account Type', width: '96px' },
  { label: 'Routing Number', width: '120px' },
  { label: 'Account Number', width: '96px' },
]

export interface BillPayBankAccountsProps {
  kybStatus: BillPayKybStatus
  legalBusinessName?: string
  account?: BillPayFundingAccount | null
  detailsState?: BillPayAccountDetailsState
  // Optional: when a flow isn't wired yet (the M1 shell), omit the handler and
  // the corresponding CTA renders disabled with a "coming soon" tooltip instead
  // of a live button that silently no-ops.
  onStartVerification?: () => void
  onAddBankAccount?: () => void
  onEnterDepositAmounts?: () => void
}

const BillPayBankAccounts: React.FC<BillPayBankAccountsProps> = ({
  kybStatus,
  legalBusinessName,
  account,
  detailsState = 'ready',
  onStartVerification,
  onAddBankAccount,
  onEnterDepositAmounts,
}) => {
  const translate = useTranslate()
  const isLocked = kybStatus !== 'verified'
  const accountStatus = account?.status ?? 'active'
  const accountStatusLabel =
    accountStatus === 'active'
      ? translate('Active')
      : accountStatus === 'linking'
        ? translate('Connecting')
        : accountStatus === 'pending'
          ? translate('Pending verification')
          : accountStatus === 'unverified'
            ? translate('Action needed')
            : translate('Review required')
  const accountFooter = isLocked ? (
    <span css={styles.lockedFooter}>{translate('Locked')}</span>
  ) : accountStatus === 'linking' ? undefined : accountStatus === 'pending' ? (
    <Button
      variant="text"
      iconRight={<ArrowIcon direction="right" />}
      onClick={onEnterDepositAmounts}
      disabled={!onEnterDepositAmounts}
      disabledTooltip={translate('Coming soon')}
    >
      {translate('Enter deposit amounts')}
    </Button>
  ) : accountStatus === 'unverified' ? (
    // Entering the amounts is the only action here. Asking for a fresh deposit
    // pair lives inside the dialog, where "they never arrived" is realised.
    <Button
      variant="text"
      iconRight={<ArrowIcon direction="right" />}
      onClick={onEnterDepositAmounts}
      disabled={!onEnterDepositAmounts}
      disabledTooltip={translate('Coming soon')}
    >
      {translate('Enter deposit amounts')}
    </Button>
  ) : account ? undefined : (
    <Button
      variant="text"
      iconLeft={<CardsNewIcon />}
      onClick={onAddBankAccount}
      disabled={!onAddBankAccount}
      disabledTooltip={translate('Coming soon')}
    >
      {translate('Add bank account')}
    </Button>
  )

  const kybBanner: CardBanner | undefined =
    kybStatus === 'unverified'
      ? {
          variant: 'warning',
          icon: <AlertShieldIcon color={tag_colorTagPendingText} />,
          title: translate('Verification required'),
          description: translate(
            "You'll be able to add a bank account once this is complete.",
          ),
        }
      : kybStatus === 'pending'
        ? {
            variant: 'warning',
            title: translate('Verifying your business'),
            description: translate('Usually a few minutes, up to 48 hours.'),
          }
        : kybStatus === 'denied'
          ? {
              variant: 'severe',
              title: translate('Verification unsuccessful'),
              description: translate(
                'Contact support for help with your business verification.',
              ),
            }
          : undefined

  return (
    <div css={styles.row}>
      {/* Left — Business Verification (KYB) */}
      <div css={styles.col}>
        <Card
          title={translate('Business Verification')}
          testId="billPayBusinessVerificationCard"
          banner={kybBanner}
          footer={
            kybStatus === 'unverified' ? (
              <Button
                variant="text"
                iconRight={<ArrowIcon direction="right" />}
                onClick={onStartVerification}
                disabled={!onStartVerification}
                disabledTooltip={translate('Coming soon')}
              >
                {translate('Start verification')}
              </Button>
            ) : undefined
          }
        >
          <p css={styles.bodyText}>
            {translate(
              "A one-time check that confirms your business's identity so you can start paying vendors.",
            )}
          </p>
          {kybStatus !== 'unverified' && (
            <div css={styles.details}>
              <Field label={translate('Status')}>
                <Pill
                  size="medium"
                  color={
                    kybStatus === 'verified'
                      ? 'highlight'
                      : kybStatus === 'denied'
                        ? 'severe'
                        : 'pending'
                  }
                >
                  {kybStatus === 'verified'
                    ? translate('Verified')
                    : kybStatus === 'denied'
                      ? translate('Denied')
                      : translate('Pending')}
                </Pill>
              </Field>
              <Field label={translate('Legal Business Name')}>
                {legalBusinessName || '—'}
              </Field>
            </div>
          )}
        </Card>
      </div>

      {/* Right — Bank Account (locked until KYB verified) */}
      <div css={styles.col}>
        <Card
          title={translate('Bank Account')}
          testId="billPayBankAccountCard"
          banner={
            isLocked
              ? {
                  variant: 'default-wcag',
                  icon: <LockedIcon width={24} height={24} />,
                  title: translate('Bank account setup is locked'),
                  description: translate(
                    'Unlocks once Business Verification is complete.',
                  ),
                }
              : undefined
          }
          footer={accountFooter}
        >
          {account ? (
            <div
              css={styles.details}
              aria-busy={detailsState === 'loading'}
              data-testid="billPayAccountDetails"
            >
              <Field label={translate('Status')}>
                <Pill
                  size="medium"
                  color={
                    accountStatus === 'active'
                      ? 'highlight'
                      : accountStatus === 'review'
                        ? 'severe'
                        : 'pending'
                  }
                >
                  {accountStatusLabel}
                </Pill>
              </Field>
              {detailsState === 'unavailable' ? (
                <AlertPanel variant="severe" role="alert">
                  {translate('Something went wrong. Please try again.')}
                </AlertPanel>
              ) : detailsState === 'loading' ? (
                ACCOUNT_DETAIL_SKELETONS.map(({ label, width }) => (
                  <div key={label} css={styles.field}>
                    <span css={styles.fieldLabel}>{translate(label)}</span>
                    <LoadingShimmer height="20px" width={width} />
                  </div>
                ))
              ) : (
                <>
                  <Field label={translate('Bank Name')}>
                    {account.bankName || '—'}
                  </Field>
                  <Field label={translate('Account Type')}>
                    {account.accountType || '—'}
                  </Field>
                  <Field label={translate('Routing Number')}>
                    {account.routingNumber || '—'}
                  </Field>
                  <Field label={translate('Account Number')}>
                    {account.last4 ? `•••• ${account.last4}` : '—'}
                  </Field>
                </>
              )}
            </div>
          ) : (
            <p css={styles.bodyText}>
              {translate(
                'This becomes your default funding account for Bill Pay — the account ACH payments to vendors are sent from.',
              )}
            </p>
          )}
        </Card>
      </div>
    </div>
  )
}

export default BillPayBankAccounts
